/**
 * StudioReports — channel performance & ops report.
 * Pipeline snapshot, publish cadence, SLA, role load, top videos.
 * Supports PDF export (client-side via browser print → save as PDF).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  FileText, Download, Loader2, ArrowLeft, BarChart3, TrendingUp,
  AlertTriangle, Users, Youtube, Sparkles,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STAGE_LABELS = {
  idea: "Idea", scripting: "Scripting", script_review: "Script Review",
  recording: "Recording", editing: "Editing", edit_review: "Edit Review",
  thumbnail: "Thumbnail", thumbnail_review: "Thumb Review",
  seo: "SEO", scheduling: "Scheduling", scheduled: "Scheduled",
  published: "Published", archived: "Archived",
};

export default function StudioReports() {
  const [params] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(params.get("channel_id") || "");
  const [rangeDays, setRangeDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/studio/channels`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const all = [...(r.data?.owned || []), ...(r.data?.shared || [])];
        setChannels(all);
        if (!activeId && all[0]) setActiveId(all[0].channel_id);
      })
      .catch((err) => alive && setError(err?.response?.data?.detail || err.message));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/studio/channels/${activeId}/reports?range_days=${rangeDays}`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => { if (alive) setError(err?.response?.data?.detail || err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeId, rangeDays]);

  const onExportPDF = () => {
    if (!printRef.current) return;
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    const today = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
    w.document.write(`
      <html><head><title>Channel Report — ${data?.channel_title || ""}</title>
      <style>
        body { font-family: -apple-system, system-ui, sans-serif; padding: 32px 40px; color: #1f1235; background: #fff; }
        .brand-bar {
          display:flex; align-items:center; justify-content:space-between;
          padding-bottom: 14px; margin-bottom: 22px;
          border-bottom: 3px solid #7c3aed;
        }
        .brand-bar .logo {
          font-family: -apple-system, system-ui, sans-serif;
          font-size: 22px; font-weight: 800;
          background: linear-gradient(135deg, #7c3aed, #ec7c5e);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .brand-bar small { color:#666; font-size:12px; }
        h1 { margin: 0 0 6px; font-size: 26px; color:#1f1235; }
        h2 {
          margin: 28px 0 10px;
          padding: 8px 12px;
          background: linear-gradient(135deg, rgba(124,58,237,0.08), rgba(236,124,94,0.06));
          border-left: 3px solid #7c3aed;
          font-size: 16px;
          color: #1f1235;
        }
        table { width: 100%; border-collapse: collapse; margin: 8px 0 14px; font-size: 13px; }
        td, th { padding: 9px 10px; border-bottom: 1px solid #eee; text-align: left; }
        th { background:#faf7ff; color:#7c3aed; font-weight:700; text-transform:uppercase; font-size:11px; letter-spacing:0.06em; }
        .kpi {
          display: inline-block;
          margin: 6px 12px 6px 0;
          padding: 14px 18px;
          background: linear-gradient(135deg, #faf7ff, #fff5ed);
          border-radius: 10px;
          border: 1px solid rgba(124,58,237,0.12);
          min-width: 140px;
        }
        .kpi strong { display: block; font-size: 26px; color:#1f1235; }
        .kpi small { color: #666; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; }
        .footer {
          margin-top: 32px; padding-top: 14px;
          border-top: 1px solid #eee;
          color:#888; font-size: 11px;
          display:flex; justify-content:space-between;
        }
        @media print { .no-print { display: none; } body { padding: 20px; } }
      </style></head><body>
        <div class="brand-bar">
          <div>
            <div class="logo">MarkeeMix Studio</div>
            <small>YouTube Operations CRM</small>
          </div>
          <div style="text-align:right;">
            <strong style="display:block; font-size:13px;">Channel Report</strong>
            <small>Generated ${today}</small>
          </div>
        </div>
        ${printRef.current.innerHTML}
        <div class="footer">
          <span>Powered by MarkeeMix Studio · markeemix.com</span>
          <span>Report range: last ${data?.range_days || 30} days</span>
        </div>
      <div class="no-print" style="margin-top:24px;text-align:center;">
        <button onclick="window.print()" style="padding:10px 22px;font-size:14px;background:linear-gradient(135deg,#7c3aed,#a78bfa);color:#fff;border:0;border-radius:8px;cursor:pointer;font-weight:600;">
          Print / Save as PDF
        </button>
      </div></body></html>
    `);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 400);
  };

  const activeChannel = channels.find((c) => c.channel_id === activeId);
  const pipelineTotal = data?.pipeline ? Object.values(data.pipeline).reduce((a, b) => a + b, 0) : 0;

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <Link to="/studio" className="studio-btn-ghost" data-testid="reports-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Studio
          </Link>
          <span className="studio-eyebrow">
            <FileText size={12} /> Reports
          </span>
          <h1 className="studio-h1">
            Channel reports & <span className="studio-h1-accent">PDF exports.</span>
          </h1>
          <p className="studio-sub">
            Pipeline snapshot, SLA breaches, team load and top performing videos —
            all exportable as a one-click PDF for your clients.
          </p>

          <div className="studio-hero-cta" style={{ flexWrap: "wrap", gap: 8 }}>
            <select
              className="studio-input"
              value={activeId}
              onChange={(e) => setActiveId(e.target.value)}
              data-testid="reports-channel-select"
              style={{ minWidth: 240 }}
            >
              <option value="">Select a channel…</option>
              {channels.map((c) => (
                <option key={c.channel_id} value={c.channel_id}>{c.title}</option>
              ))}
            </select>
            <select
              className="studio-input"
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
              data-testid="reports-range"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
            <button
              className="studio-btn-primary"
              onClick={onExportPDF}
              disabled={!data}
              data-testid="reports-export-pdf"
            >
              <Download size={16} /> Export PDF
            </button>
          </div>
        </div>

        {error && <div className="ac-error"><AlertTriangle size={16} /> {error}</div>}
        {loading && (
          <div className="studio-card studio-loading">
            <Loader2 size={22} className="ac-spin" />
            <p>Crunching numbers…</p>
          </div>
        )}

        {data && !loading && (
          <div ref={printRef} data-testid="reports-content">
            <h1 style={{ marginTop: 0 }}>{data.channel_title}</h1>
            <p className="studio-muted">Report range: last {data.range_days} days</p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "16px 0 8px" }}>
              <div className="kpi studio-card" style={{ padding: 14, minWidth: 140 }} data-testid="kpi-published">
                <small className="studio-muted">Published</small>
                <strong style={{ fontSize: 22, display: "block" }}>{data.published_count}</strong>
              </div>
              <div className="kpi studio-card" style={{ padding: 14, minWidth: 140 }} data-testid="kpi-pipeline">
                <small className="studio-muted">In pipeline</small>
                <strong style={{ fontSize: 22, display: "block" }}>{pipelineTotal}</strong>
              </div>
              <div className="kpi studio-card" style={{ padding: 14, minWidth: 140 }} data-testid="kpi-overdue">
                <small className="studio-muted">Overdue / SLA</small>
                <strong style={{ fontSize: 22, display: "block", color: data.overdue_count ? "#ef4444" : undefined }}>
                  {data.overdue_count}
                </strong>
              </div>
              <div className="kpi studio-card" style={{ padding: 14, minWidth: 140 }} data-testid="kpi-team">
                <small className="studio-muted">Team size</small>
                <strong style={{ fontSize: 22, display: "block" }}>{data.team_size}</strong>
              </div>
            </div>

            <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} /> Pipeline by stage
            </h2>
            <table>
              <thead>
                <tr><th>Stage</th><th style={{ textAlign: "right" }}>Items</th></tr>
              </thead>
              <tbody>
                {Object.entries(data.pipeline).map(([stage, n]) => (
                  <tr key={stage}>
                    <td>{STAGE_LABELS[stage] || stage}</td>
                    <td style={{ textAlign: "right" }}>{n}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={18} /> Per-role workload (open items)
            </h2>
            <table>
              <thead>
                <tr><th>Role</th><th style={{ textAlign: "right" }}>Open tasks</th></tr>
              </thead>
              <tbody>
                {Object.entries(data.role_load || {}).map(([role, n]) => (
                  <tr key={role}>
                    <td style={{ textTransform: "capitalize" }}>{role}</td>
                    <td style={{ textAlign: "right" }}>{n}</td>
                  </tr>
                ))}
                {Object.keys(data.role_load || {}).length === 0 && (
                  <tr><td colSpan={2} className="studio-muted">No team members yet.</td></tr>
                )}
              </tbody>
            </table>

            <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TrendingUp size={18} /> Top published videos
            </h2>
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th style={{ textAlign: "right" }}>Views</th>
                  <th style={{ textAlign: "right" }}>CTR</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_videos || []).map((v) => (
                  <tr key={v._id}>
                    <td>{v.title}</td>
                    <td style={{ textAlign: "right" }}>{v.views ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>{v.ctr ? `${(v.ctr * 100).toFixed(1)}%` : "—"}</td>
                  </tr>
                ))}
                {(!data.top_videos || data.top_videos.length === 0) && (
                  <tr><td colSpan={3} className="studio-muted">No published videos in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
